import React, { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { Info, Check } from 'lucide-react';
import { commonStyles } from '../styles/commonStyles';

export interface InfoTooltipItem {
  text: string;
  /** Render via dangerouslySetInnerHTML — only for i18n-owned strings with markup. */
  html?: boolean;
}

interface InfoTooltipProps {
  title: string;
  /** Check-bulleted explanation items. */
  items?: InfoTooltipItem[];
  /** Plain-paragraph variant (e.g. the Woonhuis storm-deductible hint). */
  text?: string;
  /** Optional CSS class that re-anchors the popup (e.g. 'personenauto-info-popup');
      without it the popup centers below the icon (info-tooltip-popup default). */
  popupClassName?: string;
  /** Overrides the icon wrapper (anchoring/nudges); defaults to the inline label icon. */
  wrapperStyle?: React.CSSProperties;
}

// The (i) trigger + popup used on coverage/field labels across the workflow screens.
//
// WAI-ARIA *tooltip* semantics (it describes its subject): the trigger carries
// aria-describedby + the popup has role="tooltip" — NOT aria-expanded/disclosure semantics.
// Shown while the trigger is hovered or focused; clicking/tapping focuses the button so it
// stays open after the mouse leaves (no toggle that could close what a hover just opened);
// Escape dismisses it without moving focus, and `dismissed` only resets on a fresh
// hover/focus so Escape can't be undone by simply leaving.
//
// Everything is phrasing content (spans + button) so it stays valid HTML inside a <label>.
// The shared info-tooltip-popup class adds a transparent ::before bridge (index.css) that
// keeps the popup hoverable across the gap below the icon.
export const InfoTooltip: React.FC<InfoTooltipProps> = ({ title, items, text, popupClassName, wrapperStyle }) => {
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const open = (hovered || focused) && !dismissed;
  const tooltipId = useId();
  const wrapperRef = useRef<HTMLSpanElement | null>(null);
  const popupRef = useRef<HTMLSpanElement | null>(null);
  const [fit, setFit] = useState({ flip: false, shift: 0 });

  // Document-level so Escape also works when the popup was opened by hover (button unfocused).
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => { if (e.key === 'Escape') setDismissed(true); };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open]);

  // Keep the popup inside the viewport: flip above the icon when it would run past the
  // bottom (and there is room above), and nudge it horizontally back inside the edges.
  // Recomputed on scroll/resize while open — a tap during momentum scroll would otherwise
  // lock in a measurement taken mid-scroll. Derived from the anchor + the previous shift
  // so the correction never feeds back into itself.
  useLayoutEffect(() => {
    if (!open) { setFit((prev) => (prev.flip || prev.shift ? { flip: false, shift: 0 } : prev)); return; }
    const recompute = () => {
      const tip = popupRef.current;
      const anchor = wrapperRef.current;
      if (!tip || !anchor) return;
      const a = anchor.getBoundingClientRect();
      const h = tip.offsetHeight;
      const flip = a.bottom + 8 + h > window.innerHeight - 8 && a.top - 8 - h > 8;
      const r = tip.getBoundingClientRect();
      setFit((prev) => {
        const naturalLeft = r.left - prev.shift;
        const naturalRight = r.right - prev.shift;
        let shift = 0;
        if (naturalLeft < 8) shift = Math.round(8 - naturalLeft);
        else if (naturalRight > window.innerWidth - 8) shift = Math.round(window.innerWidth - 8 - naturalRight);
        return prev.flip === flip && prev.shift === shift ? prev : { flip, shift };
      });
    };
    recompute();
    window.addEventListener('scroll', recompute, true);
    window.addEventListener('resize', recompute);
    return () => {
      window.removeEventListener('scroll', recompute, true);
      window.removeEventListener('resize', recompute);
    };
  }, [open]);

  const viewportFix: React.CSSProperties | undefined = fit.flip || fit.shift
    ? {
        ...(fit.flip ? { top: 'auto', bottom: 'calc(100% + 8px)' } : {}),
        ...(fit.shift ? { marginLeft: `${fit.shift}px` } : {}),
      }
    : undefined;

  return (
    <span
      ref={wrapperRef}
      style={wrapperStyle ?? commonStyles.infoIconInline}
      onMouseEnter={() => { setHovered(true); setDismissed(false); }}
      onMouseLeave={() => setHovered(false)}
    >
      <button
        type="button"
        aria-label={title}
        aria-describedby={open ? tooltipId : undefined}
        style={commonStyles.infoIconButton}
        onClick={(e) => e.stopPropagation()}
        onFocus={() => { setFocused(true); setDismissed(false); }}
        onBlur={() => setFocused(false)}
      >
        <Info size={18} color="var(--primary-color, #056666)" style={commonStyles.infoIcon} aria-hidden="true" />
      </button>
      {open && (
        <span
          id={tooltipId}
          role="tooltip"
          ref={popupRef}
          className={popupClassName ? `info-tooltip-popup ${popupClassName}` : 'info-tooltip-popup'}
          style={viewportFix}
        >
          <span style={commonStyles.infoPopupHeader}>
            <span style={commonStyles.infoPopupTitle}>{title}</span>
          </span>
          {text && (
            <span style={{ ...commonStyles.infoListItem, ...commonStyles.infoPopupItemText }}>{text}</span>
          )}
          {items && items.length > 0 && (
            <span style={commonStyles.infoList}>
              {items.map((item, idx) => (
                <span key={idx} style={{ ...commonStyles.infoListItem, alignItems: 'flex-start', minWidth: 0 }}>
                  <Check size={16} color="var(--success-color, #22c55e)" style={commonStyles.infoListIcon} aria-hidden="true" />
                  {item.html ? (
                    <span style={commonStyles.infoPopupItemText} dangerouslySetInnerHTML={{ __html: item.text }} />
                  ) : (
                    <span style={commonStyles.infoPopupItemText}>{item.text}</span>
                  )}
                </span>
              ))}
            </span>
          )}
        </span>
      )}
    </span>
  );
};

export default InfoTooltip;
